import React, { useMemo } from 'react';

import { portfolioData } from '../mockData';
import { useGithubContributions } from '../hooks/useGithubContributions';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

// 'YYYY-MM-DD' as a local date: `new Date(string)` would parse it as UTC and
// shift the weekday for anyone west of Greenwich.
const parseDay = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDay = (date) =>
  `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

// Buckets the flat day list into Sunday-first week columns, padding the first
// column so every row lines up with its weekday.
const buildWeeks = (days) => {
  if (!days.length) return [];

  const weeks = [];
  let current = new Array(parseDay(days[0].date).getDay()).fill(null);

  days.forEach((day) => {
    current.push(day);
    if (current.length === 7) {
      weeks.push(current);
      current = [];
    }
  });

  if (current.length) {
    weeks.push([...current, ...new Array(7 - current.length).fill(null)]);
  }

  return weeks;
};

// Month label above the column where a month first appears, skipping a month
// that only owns a sliver of the leading column.
const buildMonthLabels = (weeks) => {
  const labels = [];
  let lastMonth = null;

  weeks.forEach((week, weekIndex) => {
    const firstDay = week.find(Boolean);
    if (!firstDay) return;

    const month = parseDay(firstDay.date).getMonth();
    if (month !== lastMonth) {
      lastMonth = month;
      if (weekIndex === 0 && week.filter(Boolean).length < 4) return;
      labels.push({ month, weekIndex, key: `${month}-${weekIndex}` });
    }
  });

  return labels;
};

// Days contributed in a row, counting back from the most recent day. Today is
// skipped when it is still empty, since the day is not over yet.
const currentStreak = (days) => {
  let streak = 0;
  for (let i = days.length - 1; i >= 0; i -= 1) {
    if (days[i].count > 0) streak += 1;
    else if (i !== days.length - 1) break;
  }
  return streak;
};

const GithubContributions = () => {
  const { personal } = portfolioData;
  const username = personal.githubUsername;
  const { days, total, status } = useGithubContributions(username);

  const { weeks, monthLabels, streak, activeDays } = useMemo(() => {
    const built = buildWeeks(days);
    return {
      weeks: built,
      monthLabels: buildMonthLabels(built),
      streak: currentStreak(days),
      activeDays: days.filter((day) => day.count > 0).length
    };
  }, [days]);

  // A year spans 53 or 54 partial weeks depending on where it starts; the
  // month strip has to use the same column count as the grid to stay aligned.
  const columns = status === 'loading' ? 53 : weeks.length;

  return (
    <section id="github">
      <div className="label reveal">GitHub</div>

      <div className="gh reveal">
        <p className="gh-intro">
          {status === 'ready'
            ? `${total.toLocaleString()} contributions in the last year, spread over ${activeDays} active days.`
            : 'Commits, reviews and issues across my public repositories over the last year.'}
          {' '}
          <a href={personal.github} target="_blank" rel="noopener noreferrer">
            @{username}
          </a>
        </p>

        {status === 'error' ? (
          <p className="gh-fallback">
            The contribution graph could not be loaded right now.{' '}
            <a href={personal.github} target="_blank" rel="noopener noreferrer">
              View it on GitHub
            </a>
            .
          </p>
        ) : (
          <div className="gh-calendar-scroll">
            <div className={`gh-calendar${status === 'loading' ? ' is-loading' : ''}`}>
              <div className="gh-weekdays" aria-hidden="true">
                {WEEKDAYS.map((label, i) => (
                  <span key={i}>{label}</span>
                ))}
              </div>

              <div className="gh-grid-wrap">
                <div
                  className="gh-months"
                  aria-hidden="true"
                  style={{ gridTemplateColumns: `repeat(${columns}, var(--gh-cell))` }}
                >
                  {monthLabels.map(({ month, weekIndex, key }) => (
                    <span key={key} style={{ gridColumnStart: weekIndex + 1 }}>
                      {MONTHS[month]}
                    </span>
                  ))}
                </div>

                <div
                  className="gh-grid"
                  role="img"
                  aria-label={
                    status === 'ready'
                      ? `${total} GitHub contributions in the last year`
                      : 'Loading GitHub contributions'
                  }
                >
                  {(status === 'loading'
                    ? new Array(columns).fill(null).map(() => new Array(7).fill(null))
                    : weeks
                  ).map((week, weekIndex) => (
                    <div className="gh-week" key={weekIndex}>
                      {week.map((day, dayIndex) =>
                        day ? (
                          <span
                            key={day.date}
                            className="gh-day"
                            data-level={day.level}
                            title={`${day.count} contribution${day.count === 1 ? '' : 's'} on ${formatDay(parseDay(day.date))}`}
                          />
                        ) : (
                          <span key={dayIndex} className="gh-day is-empty" />
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {status === 'ready' && (
          <div className="gh-legend">
            <span className="gh-streak">
              {streak > 0 ? `${streak}-day streak` : 'No active streak'}
            </span>
            <span className="gh-scale">
              Less
              {[0, 1, 2, 3, 4].map((level) => (
                <span key={level} className="gh-day" data-level={level} />
              ))}
              More
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default GithubContributions;
